/**
 * Gallery Page Manager
 * Photo Gallery Section with images
 */

import { useState, useEffect } from 'react';
import { Image as ImageIcon, History, Save, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../../components/ui/button';
import { InlineText, SectionCard as InlineSectionCard } from '../../components/cms/InlineEditors';
import { BatchImageUpload } from '../../components/cms/BatchImageUpload';
import { VersionHistoryViewer } from '../../components/cms/VersionHistoryViewer';
import versionHistoryService from '../../services/versionHistoryService';
import { authService } from '../../services/authService';
import cmsApiService, { type GalleryImageDTO, type GallerySectionDTO } from '../../services/cmsApiService';

interface GalleryImage {
  [key: string]: unknown;
  id?: number;
  name?: string;
  subName?: string;
  imageUrl: string;
  _isNew?: boolean;
  _isEdited?: boolean;
}

export default function GalleryPageManager() {
  const [historyDialog, setHistoryDialog] = useState<{ type: string; id: number } | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [gallerySectionId, setGallerySectionId] = useState<number | null>(null);
  
  const [gallerySection, setGallerySection] = useState<{
    title: string;
    subtitle: string;
    buttonText?: string;
    imageUrl?: string;
    images: GalleryImage[];
  }>({
    title: 'Our Gallery',
    subtitle: 'Explore Our Space',
    buttonText: '',
    imageUrl: '',
    images: [],
  });

  useEffect(() => {
    loadGallerySection();
  }, []);

  const loadGallerySection = async () => {
    setIsLoading(true);
    
    try {
      // Use the new /gallery/by-name/GALLERY endpoint
      const data = await cmsApiService.getGallerySectionByName('GALLERY');
      
      if (!data) {
        toast('No gallery section found. Create a new one.', { icon: 'ℹ️' });
        setIsLoading(false);
        return;
      }
      
      if (data.id) {
        setGallerySectionId(data.id);
      }

      const mappedImages = (data.images || []).map((img: any) => ({
        id: img.id,
        name: img.name || '',
        subName: img.subName || '',
        imageUrl: img.imageUrl,
      })) as GalleryImage[];
      
      
      setGallerySection({
        title: data.title || 'Our Gallery',
        subtitle: data.subtitle || 'Explore Our Space',
        buttonText: data.buttonText || '',
        imageUrl: data.imageUrl || '',
        images: mappedImages,
      });
      
     
      
      toast.success('Gallery section loaded successfully');
      
    } catch (error) {
      // No gallery section found - show create message
      toast('No gallery section found. Create a new one.', { icon: 'ℹ️' });
    } finally {
      setIsLoading(false);
    }
  };

  const markChanged = () => setHasUnsavedChanges(true);

  const saveGalleryImages = async (): Promise<boolean> => {
    if (!gallerySectionId) {
      return true;
    }

    const currentImages = gallerySection.images || [];
    
    const newImages = currentImages.filter(img => !img.id || img._isNew);
    const editedImages = currentImages.filter(img => img.id && img._isEdited && !img._isNew);

    try {
      if (newImages.length > 0) {
        const addImagesDto = newImages.map(img => ({
          name: img.name || '',
          subName: img.subName || '',
          imageUrl: img.imageUrl
        }));
        
        await cmsApiService.addGalleryImages(gallerySectionId, addImagesDto);
        toast.success(`Added ${newImages.length} new image(s)`);
      }

      for (const image of editedImages) {
        const updateDto: GalleryImageDTO = {
          id: image.id,
          name: image.name || '',
          subName: image.subName || '',
          imageUrl: image.imageUrl
        };
        
        await cmsApiService.updateGalleryImageDetails(image.id!, updateDto);
      }
      
      if (editedImages.length > 0) {
        toast.success(`Updated ${editedImages.length} image(s)`);
      }

      return true;
    } catch (error) {
      toast.error('Failed to save images');
      return false;
    }
  };

  const saveAllSections = async () => {
    setIsSaving(true);
    
    try {
      const dto: Partial<GallerySectionDTO> = {
        title: gallerySection.title,
        subtitle: gallerySection.subtitle,
        buttonText: gallerySection.buttonText || '',
        imageUrl: gallerySection.imageUrl || '',
      };


      let sectionId = gallerySectionId;
      
      if (gallerySectionId) {
        const response = await cmsApiService.updateGallerySection(gallerySectionId, dto);
        toast.success('Gallery section updated successfully');
        
        await saveGalleryImages();
      } else {
        const createDto: GallerySectionDTO = {
          title: gallerySection.title,
          subtitle: gallerySection.subtitle,
          buttonText: gallerySection.buttonText || '',
          imageUrl: gallerySection.imageUrl || '',
          sectionName: 'GALLERY', // Use sectionName to identify gallery page
          images: gallerySection.images.map(img => ({
            name: img.name || '',
            subName: img.subName || '',
            imageUrl: img.imageUrl
          })),
        };
        
        const response = await cmsApiService.addGallerySection(createDto);
        
        if (response?.id) {
          setGallerySectionId(response.id);
          sectionId = response.id;
        }
        toast.success('Gallery section created successfully');
      }

      const user = await authService.getProfile();
      
      versionHistoryService.track({
        entityType: 'gallery-section',
        entityId: sectionId?.toString() || 'new',
        action: gallerySectionId ? 'update' : 'create',
        changes: { dto: { old: {}, new: dto } },
        snapshot: dto as Record<string, unknown>,
        user: user?.name || 'Admin',
      });
      
      await loadGallerySection();
      setHasUnsavedChanges(false);
      
    } catch (error) {
      toast.error('Failed to save gallery section');
    } finally {
      setIsSaving(false);
    }
  };



  const handleImagesChange = (items: GalleryImage[]) => {
    const currentImages = gallerySection.images || [];
    
    const processedItems = items.map(item => {
      if (!item.id) {
        return { ...item, _isNew: true };
      }
      
      const existingItem = currentImages.find(img => img.id === item.id);
      if (existingItem) {
        const isEdited = existingItem.imageUrl !== item.imageUrl || 
                         existingItem.name !== item.name || 
                         existingItem.subName !== item.subName;
        if (isEdited) {
          return { ...item, _isEdited: true };
        }
      }
      
      return item;
    });
    
    setGallerySection(prev => ({ ...prev, images: processedItems }));
    markChanged();
  };



  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Clock className="w-8 h-8 animate-spin mx-auto mb-2" />
          <p>Loading gallery section...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between backdrop-blur-sm bg-white/80 dark:bg-slate-900/80 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800 shadow-sm">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-700 dark:from-white dark:via-slate-100 dark:to-slate-300 bg-clip-text text-transparent">
            <ImageIcon className="w-8 h-8" />
            Gallery Page Manager
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadGallerySection} disabled={isLoading}>
            <History className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Unsaved Changes Warning Banner */}
      {hasUnsavedChanges && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
          <div className="flex items-center justify-between">
            <div className="flex">
              <Clock className="w-5 h-5 text-amber-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  You have unsaved changes
                </p>
                <p className="text-xs text-amber-600">
                  Click Save All to persist your changes.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setHasUnsavedChanges(false);
                  loadGallerySection();
                }}
                className="text-amber-800 border-amber-300 hover:bg-amber-100"
              >
                Discard
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={saveAllSections}
                disabled={isSaving}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {isSaving ? 'Saving...' : 'Save Now'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <InlineSectionCard
        title="Gallery Section"
        description="Main gallery section with title, subtitle, and images"
        icon={<ImageIcon className="w-5 h-5" />}
        onViewHistory={() => gallerySectionId && setHistoryDialog({ type: 'gallery-section', id: gallerySectionId })}
      >
        <div className="space-y-4">
          <InlineText
            label="Subtitle"
            value={gallerySection.subtitle}
            onChange={(v) => { 
              setGallerySection(prev => ({ ...prev, subtitle: v }));
              markChanged();
            }}
          />
          
          <InlineText
            label="Title"
            value={gallerySection.title}
            onChange={(v) => { 
              setGallerySection(prev => ({ ...prev, title: v }));
              markChanged();
            }}
          />

          <div className="space-y-3 pt-4 border-t">
            <BatchImageUpload
              images={gallerySection.images.map(img => ({ 
                imageUrl: img.imageUrl, 
                id: img.id 
              }))}
              onChange={(newImages: Array<{ imageUrl: string; id?: number }>) => {
                const updatedImages = newImages.map((img: { imageUrl: string; id?: number }) => ({
                  ...img,
                  name: '',
                  subName: '',
                  _isNew: !img.id,
                }));
                handleImagesChange(updatedImages);
              }}
              onDelete={async (imageId: number) => {
                try {
                  await cmsApiService.deleteGalleryImageDetails(imageId);
                  // No reload needed - BatchImageUpload handles local state update
                  return true;
                } catch (error) {
                  return false;
                }
              }}
              onUploadComplete={(urls: string[]) => {
              }}
              folder="gallery"
              maxFiles={50}
              label="Upload Gallery Images"
              description="Drag and drop multiple images here or click to browse"
            />
          </div>
        </div>
      </InlineSectionCard>

      {/* Save All Button - Bottom Right */}
      <div className="flex justify-end pt-4 border-t mt-6">
        <Button 
          onClick={saveAllSections} 
          disabled={isSaving || !hasUnsavedChanges}
          variant="blueBg"
          className="gap-2"
        >
          <Save className="w-4 h-4" />
          {isSaving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>

      {historyDialog && (
        <VersionHistoryViewer
          entityType={historyDialog.type}
          entityId={historyDialog.id}
          isOpen={true}
          onClose={() => setHistoryDialog(null)}
        />
      )}
    </div>
  );
}
